import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';
import { fromPromise } from 'xstate';

import { StripeLogger } from '@order/integrations-stripe';
import { useTelemetry } from '@order/Telemetry';

import { redirectUrl, useOpenCheckoutSession } from '../useOpenCheckoutSession';
import { useCreateCheckoutSessionMutation } from './GraphQL/CreateCheckoutSession.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Starts a session for the customer to add a payment method to their Stripe account via their browser.
 * @see {@link https://docs.stripe.com/api/checkout/sessions/create}
 */
export const useStartCheckoutSession = () => {
  const openCheckoutSession = useOpenCheckoutSession();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const [_, createCheckoutSession] = useCreateCheckoutSessionMutation();

  const startCheckoutSession = useMemo(
    () =>
      fromPromise<void, void>(async () => {
        const result = await createCheckoutSession({ input: { redirectUrl } });
        const checkoutSessionUrl = result.data?.createPaymentMethodSetupSession;

        if (!checkoutSessionUrl) {
          track('checkout.session.failure');
          StripeLogger.logCheckoutSessionFailure();
          addNoticeBanner({ text: formatMessage(messages.error), palette: 'caution' });
          return;
        }

        track('checkout.session.opened');
        StripeLogger.logCheckoutSessionOpened();
        await openCheckoutSession(checkoutSessionUrl);
      }),
    [createCheckoutSession, formatMessage, addNoticeBanner, track, openCheckoutSession],
  );

  return { startCheckoutSession };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  error: {
    defaultMessage: 'Something went wrong starting your checkout session.',
    description: 'Bag | Checkout Session | Error',
  },
});
